import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  Input,
  ViewChild,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { FormControl } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { DatePickerModal } from '../modals/date-picker-modal/date-picker-modal';
import { Modal } from '../../../core/services/modal';

@Component({
  selector: 'app-date-picker',
  imports: [CommonModule, TranslatePipe],
  templateUrl: './date-picker.html',
  styleUrl: './date-picker.css',
})
export class DatePicker implements OnInit, OnDestroy {
  @Input() label = 'Select date';
  @Input() placeholder = 'DD/MM/YYYY';
  @Input() modalTitle = 'Select Date';
  @Input() control: FormControl<any> | null = null;
  @Input() defaultSelection: 'year' | 'month' | 'day' | 'all' = 'all';
  @Input() minYear?: number;
  @Input() maxYear?: number;
  @Input() customClass = '';
  @Input() showInlineError?: boolean = false;

  /** Error handling */
  @Input() errorMessage: string | null = null;

  @ViewChild('input') inputRef!: ElementRef<HTMLInputElement>;

  selectedDate: Date | null = null;
  displayValue = '';

  private destroy$ = new Subject<void>();

  constructor(private modalService: Modal) { }

  ngOnInit() {
    // If prefilled control value exists
    if (this.control?.value) {
      if (this.defaultSelection === 'month') {
        this.selectedDate = this.control.value;
      } else if (this.defaultSelection === 'year') {

      } else {
        const parsed = this.parseDate(this.control.value);
        if (parsed) {
          this.selectedDate = parsed;
          this.displayValue = this.formatDate(parsed);
        }
      }
    }

    // Watch control value changes
    this.control?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((val) => {
      const parsed = this.parseDate(val);
      if (parsed) {
        this.selectedDate = parsed;
        this.displayValue = this.formatDate(parsed);
      } else {
        this.displayValue = val ?? '';
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }


  /** Called when user types manually in input */
  onInputChange(value: string) {
    this.displayValue = value;
    const parsedDate = this.parseDate(value);
    if (parsedDate) {
      this.selectedDate = parsedDate;
      this.control?.setValue(value, { emitEvent: false });
      this.control?.markAsDirty();
    } else {
      // Invalid format: mark control invalid if validator exists
      this.control?.setValue(value, { emitEvent: false });
    }
  }

  /** Parses both 12/Jan/2025 and 12/01/2025 */
  parseDate(input: string): Date | null {
    if (!input) return null;

    // Try DD/MM/YYYY
    const num = input.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
    if (num) {
      const [, dayStr, monthStr, yearStr] = num;
      const d = new Date(parseInt(yearStr, 10), parseInt(monthStr, 10) - 1, parseInt(dayStr, 10));
      return this.isValidDate(d) ? d : null;
    }

    // Try DD/Mon/YYYY
    const str = input.match(/^(\d{1,2})[\/\-]([A-Za-z]{3})[\/\-](\d{4})$/);
    if (str) {
      const [, dayStr, monthStr, yearStr] = str;
      const monthMap: Record<string, number> = {
        jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
        jul: 6, aug: 7, sep: 8, oct: 9, nov: 10, dec: 11,
      };
      const month = monthMap[monthStr.toLowerCase()];
      if (month !== undefined) {
        const d = new Date(parseInt(yearStr, 10), month, parseInt(dayStr, 10));
        return this.isValidDate(d) ? d : null;
      }
    }

    return null;
  }

  isValidDate(d: Date): boolean {
    return d instanceof Date && !isNaN(d.getTime());
  }

  formatDate(date: Date): string {
    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString('default', { month: 'short' }); // e.g. "Oct"
    const year = date.getFullYear();
    return `${day}/${month}/${year}`; // 30/Oct/2059
  }

  /** When calendar icon is clicked */
  onCalendarClick() {
    const modalOptions: any = {
      backdropClosable: false,
      showCancelButton: true,
      headerTitle: this.modalTitle,
      defaultSelection: this.defaultSelection,
      prefilledDate: this.selectedDate ?? null,
      minYear: this.minYear,
      maxYear: this.maxYear,
      onConfirm: (result: any) => {
        console.log(result, "result")
        if (result) {
          this.selectedDate = result;

          if (this.defaultSelection === 'year' || this.defaultSelection === 'month') {
            this.displayValue = result
          } else this.displayValue = this.formatDate(result);

          this.control?.setValue(this.displayValue);
        }
      },
    };

    this.modalService.open(DatePickerModal, modalOptions);
  }


  get hasErrorState(): boolean | undefined {
    return this.control?.invalid && (this.control?.touched || this.control?.dirty);
  }

  private readonly _autoId =
    `input-${Math.random().toString(36).slice(2, 9)}`;

  get inputId(): string {
    return this._autoId;
  }
}
